/**
 * The canopy height under the cursor, read back from the CHM tile's colour.
 *
 * The tiler paints a CHM with viridis stretched over the raster's value range (floored at 0 m,
 * see `DEM_RENDERING`), so a pixel's colour inverts through the same table to a position on the
 * ramp, and the position to a height. It is a reading, not a measurement: one 256th of the
 * stretch per step, the top 2 % of heights saturate, and crown edges blend colours.
 */
import { RASTER_TILE_SIZE, rasterTileUrl, type LayerRef } from './layers';
import { tileCache as sharedTileCache, type TileCache } from './tileCache';
import type { TokenSource } from './tokenSource';
import { VIRIDIS_TABLE } from './viridisTable';

/** The tiles the map draws, so a reading shares their cache; an older tiler's 256 px tile is scaled. */
export const TILE_SIZE = RASTER_TILE_SIZE;
/** Squared RGB distance beyond which a colour is not on the ramp (a blend at an edge, the basemap). */
const OFF_RAMP = 24 * 24;
const CACHE_LIMIT = 64;

export interface TileAddress {
  x: number;
  y: number;
  /** The pixel inside the tile, 0..TILE_SIZE - 1. */
  px: number;
  py: number;
}

/** Web Mercator tile holding `lng, lat` at zoom `z`, and the pixel of the point inside it. */
export function tileAt(lng: number, lat: number, z: number): TileAddress {
  const n = 2 ** z;
  const fx = ((lng + 180) / 360) * n;
  const rad = (lat * Math.PI) / 180;
  const fy = ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * n;
  const x = Math.floor(fx);
  const y = Math.floor(fy);
  return { x, y, px: Math.min(TILE_SIZE - 1, Math.floor((fx - x) * TILE_SIZE)), py: Math.min(TILE_SIZE - 1, Math.floor((fy - y) * TILE_SIZE)) };
}

/** The ramp position (0..1) of a colour, or `null` when it is not a ramp colour. */
export function invertViridis(r: number, g: number, b: number): number | null {
  let best = -1;
  let bestDistance = Infinity;
  for (let i = 0; i < VIRIDIS_TABLE.length; i++) {
    const [tr, tg, tb] = VIRIDIS_TABLE[i]!;
    const d = (r - tr) ** 2 + (g - tg) ** 2 + (b - tb) ** 2;
    if (d < bestDistance) {
      bestDistance = d;
      best = i;
    }
  }
  return bestDistance <= OFF_RAMP ? best / (VIRIDIS_TABLE.length - 1) : null;
}

/** The height a ramp position stands for; the last step means "at least this much". */
export function heightOf(t: number, [low, high]: [number, number]): { value: number; saturated: boolean } {
  return { value: low + t * (high - low), saturated: t >= 1 };
}

export interface ChmSampler {
  /** The ramp position under `lng, lat`, `null` over nodata or off the raster. */
  sample(ref: LayerRef, baseUrl: string, z: number, lng: number, lat: number): Promise<number | null>;
}

/** Decodes tiles into pixel data once and reads single pixels out of them. */
export function createChmSampler(tokenSource: TokenSource, cache: TileCache = sharedTileCache): ChmSampler {
  const tiles = new Map<string, Promise<ImageData | null>>();

  const decode = async (url: string): Promise<ImageData | null> => {
    const signed = tokenSource.transformRequest(url)?.url ?? url;
    const bitmap = await createImageBitmap(new Blob([await cache.fetch(signed)]));
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d');
    if (!context) return null;
    context.drawImage(bitmap, 0, 0);
    return context.getImageData(0, 0, bitmap.width, bitmap.height);
  };

  const tile = (url: string): Promise<ImageData | null> => {
    let pending = tiles.get(url);
    if (!pending) {
      pending = decode(url).catch(() => null);
      if (tiles.size >= CACHE_LIMIT) tiles.delete(tiles.keys().next().value!);
      tiles.set(url, pending);
    }
    return pending;
  };

  return {
    async sample(ref, baseUrl, z, lng, lat) {
      const at = tileAt(lng, lat, z);
      const image = await tile(rasterTileUrl(baseUrl, ref, z, at.x, at.y));
      if (!image) return null;
      const scale = image.width / TILE_SIZE;
      const offset = (Math.floor(at.py * scale) * image.width + Math.floor(at.px * scale)) * 4;
      const [r, g, b, a] = [image.data[offset]!, image.data[offset + 1]!, image.data[offset + 2]!, image.data[offset + 3]!];
      if (a === 0) return null;
      return invertViridis(r, g, b);
    },
  };
}
